// Mirrors cgt-backend/src/inventory/storage/storage-tree.ts gridCellLabels —
// duplicated by design per the root CLAUDE.md; change both sides together.

import type { SlotLabelScheme } from '@/types/storage'
import { rowLabel } from './storageTree'

/**
 * Ceiling on cells minted from a single declared grid. Mirrors
 * MAX_GRID_POSITIONS on the server.
 */
export const MAX_GRID_POSITIONS = 5000

export interface GridCellLabel {
  code: string
  gridRow: number
  gridColumn: number
}

export interface GridLabelOptions {
  rows: number
  columns: number
  scheme: SlotLabelScheme
  prefix?: string
}

/** Default SEQUENTIAL prefixes by minted kind — matching the design note's
 *  canonical address R03 / C05 / P18. */
export const DEFAULT_LABEL_PREFIX: Record<string, string> = {
  RACK: 'R',
  BOX: 'C',
  POSITION: 'P',
}

const PREFIX_PATTERN = /^[A-Za-z0-9]{1,8}$/

function assertValidGrid(rows: number, columns: number): void {
  if (!Number.isInteger(rows) || !Number.isInteger(columns)) {
    throw new Error('Grid rows and columns must be whole numbers')
  }
  if (rows < 1 || columns < 1) {
    throw new Error('Grid rows and columns must both be at least 1')
  }
  if (rows * columns > MAX_GRID_POSITIONS) {
    throw new Error(
      `A grid of ${rows}×${columns} would mint ${rows * columns} positions, above the ${MAX_GRID_POSITIONS} limit`,
    )
  }
}

/**
 * The labels a declared grid mints, with their structural coordinates.
 *
 * The label is presentation; (gridRow, gridColumn) is the identity. That is
 * what lets ROW_COLUMN (A1…D5, the SLAS cryobox convention) and SEQUENTIAL
 * (R01…R10, the design note's own addresses) be per-unit choices instead of
 * a decision baked into every stored code.
 */
export function gridCellLabels({ rows, columns, scheme, prefix }: GridLabelOptions): GridCellLabel[] {
  assertValidGrid(rows, columns)
  const cells: GridCellLabel[] = []
  if (scheme === 'SEQUENTIAL') {
    const p = prefix ?? 'P'
    if (!PREFIX_PATTERN.test(p)) throw new Error(`Invalid label prefix: "${p}"`)
    // Zero-padded to the width of the total count, matching R03/C05 in the
    // design note — labels sort correctly as plain strings.
    const width = String(rows * columns).length
    for (let r = 1; r <= rows; r += 1) {
      for (let c = 1; c <= columns; c += 1) {
        const index = (r - 1) * columns + c
        cells.push({ code: `${p}${String(index).padStart(width, '0')}`, gridRow: r, gridColumn: c })
      }
    }
  } else {
    for (let r = 1; r <= rows; r += 1) {
      for (let c = 1; c <= columns; c += 1) {
        cells.push({ code: `${rowLabel(r - 1)}${c}`, gridRow: r, gridColumn: c })
      }
    }
  }
  return cells
}
